import { Heart, Wine, Cake, Tent, Utensils, Music, Sparkles } from "lucide-react";
import { useContent } from "../context/ContentContext";

const ICONS = {
  heart: Heart,
  wine: Wine,
  cake: Cake,
  tent: Tent,
  utensils: Utensils,
  music: Music,
  sparkles: Sparkles,
};

export const Timeline = () => {
  const { content } = useContent();
  const events = [...(content.timeline_events || [])].sort((a, b) => {
    const toMin = (t) => {
      if (!t) return 0;
      const [h, m] = String(t).split(":").map((n) => parseInt(n, 10) || 0);
      return h * 60 + m;
    };
    return toMin(a.time) - toMin(b.time);
  });

  return (
    <section
      id="zeitplan"
      data-testid="section-timeline"
      className="py-24 md:py-36 bg-white relative"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-16 md:mb-20">
          <p className="text-sage-500 uppercase tracking-[0.35em] text-xs mb-6">
            {content.timeline_eyebrow}
          </p>
          <h2
            data-testid="timeline-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-sage-800"
          >
            {content.timeline_title_prefix}{" "}
            <span className="font-script text-sage-500 text-5xl sm:text-6xl lg:text-7xl">
              {content.timeline_title_script}
            </span>
          </h2>
          <p className="mt-6 text-sage-600 max-w-xl mx-auto text-sm sm:text-base">
            {content.timeline_subtitle}
          </p>
        </div>

        {/* MOBILE TIMELINE */}
        <ol className="md:hidden relative max-w-md mx-auto pl-2">
          <div
            className="absolute left-8 top-2 bottom-2 w-px bg-sage-200"
            aria-hidden="true"
          />
          {events.map((e, idx) => {
            const Icon = ICONS[e.icon] || Heart;
            return (
              <li
                key={`m-${e.time}-${idx}`}
                data-testid={`timeline-event-${idx}`}
                className="relative flex gap-5 pb-10 last:pb-0"
              >
                <div className="relative shrink-0 w-16 flex justify-center">
                  <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-sage-500 text-white shadow-md ring-4 ring-white">
                    <Icon size={20} strokeWidth={1.6} />
                  </span>
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <EventCard event={e} idx={idx} />
                </div>
              </li>
            );
          })}
        </ol>

        {/* DESKTOP TIMELINE — alternating */}
        <ol className="hidden md:block relative max-w-3xl mx-auto">
          <div
            className="absolute left-1/2 top-0 bottom-0 w-px bg-sage-200 -translate-x-1/2"
            aria-hidden="true"
          />
          {events.map((e, idx) => {
            const Icon = ICONS[e.icon] || Heart;
            const onRight = idx % 2 === 1;
            return (
              <li
                key={`d-${e.time}-${idx}`}
                className="relative grid grid-cols-2 gap-12 items-center pb-12 last:pb-0"
              >
                {onRight ? (
                  <>
                    <div />
                    <div className="pl-8">
                      <EventCard event={e} idx={idx} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pr-8">
                      <EventCard event={e} idx={idx} align="right" />
                    </div>
                    <div />
                  </>
                )}
                <span
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-16 w-16 items-center justify-center rounded-full bg-sage-500 text-white shadow-lg ring-8 ring-white z-10"
                >
                  <Icon size={22} strokeWidth={1.6} />
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

const EventCard = ({ event, idx, align }) => (
  <div
    data-testid={`timeline-content-${idx}`}
    className="bg-sage-50 rounded-2xl border border-sage-200 p-5 md:p-7 shadow-sm hover:shadow-md transition-shadow"
  >
    <div
      className={`text-sage-500 font-serif text-2xl md:text-3xl tracking-tight leading-none ${
        align === "right" ? "md:text-right" : ""
      }`}
    >
      {event.time}
    </div>
    <h3
      className={`mt-2 text-sage-800 font-serif text-xl md:text-2xl leading-snug ${
        align === "right" ? "md:text-right" : ""
      }`}
    >
      {event.title}
    </h3>
    <p
      className={`mt-2 md:mt-3 text-sage-600 text-sm md:text-base leading-relaxed ${
        align === "right" ? "md:text-right" : ""
      }`}
    >
      {event.description}
    </p>
  </div>
);

export default Timeline;
